<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { DEFAULT_TIMEZONE } from '@/constants/timezones'
import type { PublicVenue, RaceType, StoreRacePayload, Venue } from '@/types/events'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import VenueSelect from './VenueSelect.vue'
import RacePriceFeePreview from './RacePriceFeePreview.vue'
import { normalisePreviewAmount } from '@/utils/platformFeeAmount'
import { venueLabel } from '@/utils/venueLabel'

interface RaceFormData {
  name: string
  type: RaceType | ''
  base_price: string
  distance: string
  elevation: string
  start_venue_id: number | null
  end_venue_id: number | null
  start_time: string
  cutoff: string
  max_participants: string
  allow_waiting_list: boolean
  payment_deadline_hours: string
  registrations_closing_time: string
  registrations_open: boolean
  sort_order: string
}

const RACE_TYPES: RaceType[] = [
  'road',
  'trail',
  'track',
  'duathlon',
  'triathlon',
  'cycling',
  'virtual',
  'other',
]

const props = defineProps<{
  modelValue: RaceFormData
  loading: boolean
  errors: Record<string, string[]>
  organisationId: number
  isEdit?: boolean
  /** Time zone of the edition venue (races without a start venue inherit it); null/absent = no edition venue. */
  editionVenueTimezone?: string | null
  /** Edit mode: the resolved zone the race was loaded with (own start venue, else the edition venue). */
  initialStartTimezone?: string
  /** The edition venue, named in the "use the edition's venue" option; null/absent = no edition venue. */
  editionVenue?: PublicVenue | null
  /** Edit mode: the resolved start venue the race was loaded with (own start venue, else the edition venue). */
  initialStartVenue?: PublicVenue | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: RaceFormData]
  submit: [data: StoreRacePayload]
  cancel: []
}>()

const { t } = useI18n()

// null = the start venue was not touched in this session; { zone: null } = cleared (inherits the edition venue).
const startVenueOverride = ref<{ zone: string | null } | null>(null)
// undefined = the start venue was not touched in this session; null = cleared (inherits the edition venue).
const selectedStartVenue = ref<PublicVenue | null | undefined>(undefined)
// Set when the selected start venue was edited in place, so its new zone is not reported as a move.
const updatedInitialTimezone = ref<string | null>(null)

const fallbackTimezone = computed<string>(() => props.editionVenueTimezone ?? DEFAULT_TIMEZONE)

const initialZone = computed<string | undefined>(() => updatedInitialTimezone.value ?? props.initialStartTimezone)

const startTimezone = computed<string>(() => {
  if (startVenueOverride.value === null) {
    return props.initialStartTimezone ?? fallbackTimezone.value
  }
  return startVenueOverride.value.zone ?? fallbackTimezone.value
})

const showStartVenueTimezoneWarning = computed<boolean>(
  () => props.isEdit === true && initialZone.value !== undefined && startTimezone.value !== initialZone.value,
)

/** Append the venue name in brackets to an empty-option label, when there is a name to show. */
function withVenueName(label: string, venue: PublicVenue | null | undefined): string {
  const name = venueLabel(venue)
  return name === null ? label : `${label} (${name})`
}

// The venue the race actually starts at: the picked one, else the one it was loaded with, else the edition venue.
const resolvedStartVenue = computed<PublicVenue | null>(() => {
  if (selectedStartVenue.value === undefined) {
    return props.initialStartVenue ?? props.editionVenue ?? null
  }
  return selectedStartVenue.value ?? props.editionVenue ?? null
})

const startVenueEmptyLabel = computed<string>(() =>
  withVenueName(t('races.form.venue_inherit_option'), props.editionVenue),
)

const endVenueEmptyLabel = computed<string>(() =>
  withVenueName(t('races.form.end_venue_none_option'), resolvedStartVenue.value),
)

const showWaitingList = computed(() => props.modelValue.max_participants !== '')

// Same visibility rule as RacePriceFeePreview, so its grid cell is not reserved while it renders nothing.
const showFeePreview = computed<boolean>(() => normalisePreviewAmount(props.modelValue.base_price) !== null)

function handleStartVenueSelected(venue: Venue | null): void {
  startVenueOverride.value = { zone: venue?.timezone ?? null }
  selectedStartVenue.value = venue
}

function handleStartVenueUpdated(venue: Venue): void {
  if (venue.id !== props.modelValue.start_venue_id) return
  startVenueOverride.value = { zone: venue.timezone }
  selectedStartVenue.value = venue
  updatedInitialTimezone.value = venue.timezone
}

function update(field: keyof RaceFormData, value: string | number | boolean | null): void {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

/**
 * Clear the venue selection when the venue it pointed at has just been deleted.
 * The backend nulls the foreign key, but a form still holding the stale id in
 * memory would 422 on its next save.
 */
function handleVenueDeleted(field: 'start_venue_id' | 'end_venue_id', venueId: number): void {
  if (props.modelValue[field] === venueId) {
    update(field, null)
    if (field === 'start_venue_id') {
      startVenueOverride.value = { zone: null }
      selectedStartVenue.value = null
    }
  }
}

function formatDatetimeForApi(value: string): string {
  if (!value) return ''
  // datetime-local gives 'YYYY-MM-DDTHH:mm', API needs 'YYYY-MM-DD HH:mm:ss'
  return value.replace('T', ' ') + ':00'
}

function handleSubmit(): void {
  if (props.modelValue.type === '') return

  const payload: StoreRacePayload = {
    name: props.modelValue.name,
    type: props.modelValue.type as RaceType,
    base_price: parseFloat(props.modelValue.base_price),
    distance: parseFloat(props.modelValue.distance),
    elevation: props.modelValue.elevation !== '' ? parseInt(props.modelValue.elevation, 10) : null,
    // Always sent explicitly, including as null: the backend rules are
    // `sometimes|nullable`, so omitting the key would leave the stored value
    // untouched instead of clearing it.
    start_venue_id: props.modelValue.start_venue_id,
    end_venue_id: props.modelValue.end_venue_id,
    start_time: formatDatetimeForApi(props.modelValue.start_time),
    cutoff: props.modelValue.cutoff !== '' ? parseInt(props.modelValue.cutoff, 10) : null,
    max_participants:
      props.modelValue.max_participants !== ''
        ? parseInt(props.modelValue.max_participants, 10)
        : null,
    allow_waiting_list: props.modelValue.allow_waiting_list,
    payment_deadline_hours:
      props.modelValue.payment_deadline_hours !== ''
        ? parseInt(props.modelValue.payment_deadline_hours, 10)
        : null,
    registrations_closing_time: props.modelValue.registrations_closing_time
      ? formatDatetimeForApi(props.modelValue.registrations_closing_time)
      : null,
    registrations_open: props.modelValue.registrations_open,
    sort_order: props.modelValue.sort_order !== '' ? parseInt(props.modelValue.sort_order, 10) : undefined,
  }
  emit('submit', payload)
}
</script>

<template>
  <form novalidate class="max-w-3xl space-y-6" @submit.prevent="handleSubmit">
    <!-- Section 1: Basics -->
    <section
      aria-labelledby="race-section-basics"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
      data-testid="race-section-basics"
    >
      <h2 id="race-section-basics" class="text-base font-semibold text-white mb-1">
        {{ t('races.form.section_basics_title') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">{{ t('races.form.section_basics_description') }}</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Name -->
        <div class="sm:col-span-2">
          <label for="race-name" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.name_label') }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input
            id="race-name"
            :value="modelValue.name"
            type="text"
            :placeholder="t('races.form.name_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['name'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('name', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['name']" class="mt-1 text-xs text-red-400">{{ errors['name'][0] }}</p>
        </div>

        <!-- Type -->
        <div>
          <label for="race-type" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.type_label') }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <select
            id="race-type"
            :value="modelValue.type"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['type'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @change="update('type', ($event.target as HTMLSelectElement).value)"
          >
            <option value="" disabled>{{ t('races.form.type_placeholder') }}</option>
            <option v-for="raceType in RACE_TYPES" :key="raceType" :value="raceType">
              {{ t(`races.form.type_${raceType}`) }}
            </option>
          </select>
          <p v-if="errors['type']" class="mt-1 text-xs text-red-400">{{ errors['type'][0] }}</p>
        </div>

        <!-- Sort order -->
        <div>
          <label for="race-sort-order" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.sort_order_label') }}
          </label>
          <input
            id="race-sort-order"
            :value="modelValue.sort_order"
            type="number"
            min="0"
            step="1"
            placeholder="0"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['sort_order'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('sort_order', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['sort_order']" class="mt-1 text-xs text-red-400">{{ errors['sort_order'][0] }}</p>
          <p class="mt-1 text-xs text-slate-500">{{ t('races.form.sort_order_hint') }}</p>
        </div>
      </div>
    </section>

    <!-- Section 2: Start, finish and time (venues first: the start venue decides the time zone) -->
    <section
      aria-labelledby="race-section-schedule"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
      data-testid="race-section-schedule"
    >
      <h2 id="race-section-schedule" class="text-base font-semibold text-white mb-1">
        {{ t('races.form.section_schedule_title') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">{{ t('races.form.section_schedule_description') }}</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Start venue (optional: empty inherits the edition's venue) -->
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.start_venue_label') }}
          </label>
          <VenueSelect
            :model-value="modelValue.start_venue_id"
            :organisation-id="organisationId"
            :allow-empty="true"
            field-name="start_venue_id"
            :empty-label="startVenueEmptyLabel"
            :aria-label="t('races.form.start_venue_label')"
            :errors="errors"
            @update:model-value="update('start_venue_id', $event)"
            @venue-selected="handleStartVenueSelected"
            @venue-updated="handleStartVenueUpdated"
            @deleted="handleVenueDeleted('start_venue_id', $event)"
          />
          <p class="mt-1 text-xs text-slate-500">{{ t('races.form.start_venue_hint') }}</p>
          <p
            v-if="showStartVenueTimezoneWarning"
            class="mt-1 text-xs text-amber-400"
            role="alert"
            data-testid="race-start-venue-timezone-warning"
          >
            {{ t('races.form.start_venue_timezone_warning', { from: initialZone, to: startTimezone }) }}
          </p>
        </div>

        <!-- Finish venue (optional: empty means it finishes where it starts) -->
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.end_venue_label') }}
          </label>
          <VenueSelect
            :model-value="modelValue.end_venue_id"
            :organisation-id="organisationId"
            :allow-empty="true"
            field-name="end_venue_id"
            :empty-label="endVenueEmptyLabel"
            :aria-label="t('races.form.end_venue_label')"
            :errors="errors"
            @update:model-value="update('end_venue_id', $event)"
            @deleted="handleVenueDeleted('end_venue_id', $event)"
          />
          <p class="mt-1 text-xs text-slate-500">{{ t('races.form.end_venue_hint') }}</p>
        </div>

        <!-- Start time -->
        <div>
          <label for="race-start-time" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.start_time_label') }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input
            id="race-start-time"
            :value="modelValue.start_time"
            type="datetime-local"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['start_time'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('start_time', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['start_time']" class="mt-1 text-xs text-red-400">{{ errors['start_time'][0] }}</p>
          <p class="mt-1 text-xs text-slate-500" data-testid="race-times-timezone-hint">{{ t('races.form.times_in_timezone', { timezone: startTimezone }) }}</p>
        </div>

        <!-- Cutoff -->
        <div>
          <label for="race-cutoff" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.cutoff_label') }}
          </label>
          <input
            id="race-cutoff"
            :value="modelValue.cutoff"
            type="number"
            min="1"
            step="1"
            :placeholder="t('races.form.cutoff_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['cutoff'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('cutoff', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['cutoff']" class="mt-1 text-xs text-red-400">{{ errors['cutoff'][0] }}</p>
        </div>
      </div>
    </section>

    <!-- Section 3: Course -->
    <section
      aria-labelledby="race-section-course"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
      data-testid="race-section-course"
    >
      <h2 id="race-section-course" class="text-base font-semibold text-white mb-1">
        {{ t('races.form.section_course_title') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">{{ t('races.form.section_course_description') }}</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Distance -->
        <div>
          <label for="race-distance" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.distance_label') }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input
            id="race-distance"
            :value="modelValue.distance"
            type="number"
            min="0"
            step="0.001"
            :placeholder="t('races.form.distance_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['distance'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('distance', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['distance']" class="mt-1 text-xs text-red-400">{{ errors['distance'][0] }}</p>
        </div>

        <!-- Elevation -->
        <div>
          <label for="race-elevation" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.elevation_label') }}
          </label>
          <input
            id="race-elevation"
            :value="modelValue.elevation"
            type="number"
            min="0"
            step="1"
            :placeholder="t('races.form.elevation_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['elevation'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('elevation', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['elevation']" class="mt-1 text-xs text-red-400">{{ errors['elevation'][0] }}</p>
        </div>
      </div>
    </section>

    <!-- Section 4: Price and payment (DOM order: price, fee preview, payment deadline) -->
    <section
      aria-labelledby="race-section-price"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
      data-testid="race-section-price"
    >
      <h2 id="race-section-price" class="text-base font-semibold text-white mb-1">
        {{ t('races.form.section_price_title') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">{{ t('races.form.section_price_description') }}</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Base price -->
        <div class="sm:col-start-1 sm:row-start-1">
          <label for="race-base-price" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.base_price_label') }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input
            id="race-base-price"
            :value="modelValue.base_price"
            type="number"
            min="0"
            step="0.01"
            :placeholder="t('races.form.base_price_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['base_price'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('base_price', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['base_price']" class="mt-1 text-xs text-red-400">{{ errors['base_price'][0] }}</p>
          <p class="mt-1 text-xs text-slate-500">{{ t('races.form.base_price_standard_hint') }}</p>
        </div>

        <!-- Fee preview (spans both columns under the price row on sm+; hidden until a price is entered) -->
        <div v-if="showFeePreview" class="sm:col-span-2 sm:col-start-1 sm:row-start-2">
          <RacePriceFeePreview
            :organisation-id="organisationId"
            :price="modelValue.base_price"
          />
        </div>

        <!-- Payment deadline hours -->
        <div class="sm:col-start-2 sm:row-start-1">
          <label for="race-payment-deadline" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.payment_deadline_hours_label') }}
          </label>
          <input
            id="race-payment-deadline"
            :value="modelValue.payment_deadline_hours"
            type="number"
            min="1"
            max="720"
            step="1"
            placeholder="48"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['payment_deadline_hours'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('payment_deadline_hours', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['payment_deadline_hours']" class="mt-1 text-xs text-red-400">{{ errors['payment_deadline_hours'][0] }}</p>
          <p class="mt-1 text-xs text-slate-500">{{ t('races.form.payment_deadline_hours_hint') }}</p>
        </div>
      </div>

      <!-- Pricing tiers (rendered by the page; contains only type="button" controls) -->
      <slot name="price-tiers" :timezone="startTimezone" />
    </section>

    <!-- Section 5: Registrations -->
    <section
      aria-labelledby="race-section-registrations"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
      data-testid="race-section-registrations"
    >
      <h2 id="race-section-registrations" class="text-base font-semibold text-white mb-1">
        {{ t('races.form.section_registrations_title') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">{{ t('races.form.section_registrations_description') }}</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Registrations open -->
        <div class="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50 border border-slate-700 sm:col-span-2">
          <input
            id="race-registrations-open"
            :checked="modelValue.registrations_open"
            type="checkbox"
            class="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer"
            @change="update('registrations_open', ($event.target as HTMLInputElement).checked)"
          />
          <div>
            <label for="race-registrations-open" class="text-sm font-medium text-slate-300 cursor-pointer">
              {{ t('races.form.registrations_open_label') }}
            </label>
            <p class="text-xs text-slate-500 mt-0.5">{{ t('races.form.registrations_open_hint') }}</p>
          </div>
        </div>

        <!-- Registrations closing time -->
        <div>
          <label for="race-reg-closing" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.registrations_closing_time_label') }}
          </label>
          <input
            id="race-reg-closing"
            :value="modelValue.registrations_closing_time"
            type="datetime-local"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['registrations_closing_time'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('registrations_closing_time', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['registrations_closing_time']" class="mt-1 text-xs text-red-400">{{ errors['registrations_closing_time'][0] }}</p>
          <p class="mt-1 text-xs text-slate-500">{{ t('races.form.registrations_closing_time_hint') }}</p>
          <p class="mt-1 text-xs text-slate-500" data-testid="race-closing-timezone-hint">{{ t('races.form.times_in_timezone', { timezone: startTimezone }) }}</p>
        </div>

        <!-- Max participants, with the waiting list option directly beneath it -->
        <div>
          <label for="race-max-participants" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('races.form.max_participants_label') }}
          </label>
          <input
            id="race-max-participants"
            :value="modelValue.max_participants"
            type="number"
            min="1"
            step="1"
            :placeholder="t('races.form.max_participants_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors['max_participants'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            @input="update('max_participants', ($event.target as HTMLInputElement).value)"
          />
          <p v-if="errors['max_participants']" class="mt-1 text-xs text-red-400">{{ errors['max_participants'][0] }}</p>

          <!-- Allow waiting list (only when max_participants set) -->
          <div v-if="showWaitingList" class="mt-3 flex items-center gap-2">
            <input
              id="race-allow-waiting-list"
              :checked="modelValue.allow_waiting_list"
              type="checkbox"
              class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
              @change="update('allow_waiting_list', ($event.target as HTMLInputElement).checked)"
            />
            <label for="race-allow-waiting-list" class="text-sm text-slate-300 cursor-pointer">
              {{ t('races.form.allow_waiting_list_label') }}
            </label>
          </div>
        </div>
      </div>
    </section>

    <!-- Actions -->
    <div class="flex items-center gap-3">
      <button
        type="submit"
        :disabled="loading"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors flex items-center gap-2"
      >
        <AppSpinner v-if="loading" size="button" />
        {{ loading ? t('common.loading') : (isEdit ? t('races.form.submit_update') : t('races.form.submit_create')) }}
      </button>
      <button
        type="button"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="emit('cancel')"
      >
        {{ t('races.form.cancel') }}
      </button>
    </div>
  </form>
</template>
